import type { ProductContext } from "../types";
import { extractSlugPathFromUrl, findProductJsonLd, hasProductJsonLd } from "./json-ld";
import type { PlatformAdapter } from "./types";

declare global {
  interface Window {
    Opentray?: unknown;
    Tray?: unknown;
  }
}

// A Tray não documenta nenhuma variável JS global ou classe de body padronizada
// para identificar a página de produto (temas são livremente customizáveis em
// Twig no servidor) — então esses dois sinais são heurísticas, não garantia.
// "-p<digitos>" é o padrão observado nas URLs de produto do tema padrão da Tray.
function matchesProductUrlOrBodyClass(): boolean {
  return (
    document.body.classList.contains("produto") || /-p\d+(\/|$)/.test(window.location.pathname)
  );
}

export const trayAdapter: PlatformAdapter = {
  isCurrentPlatform() {
    if (typeof window.Opentray !== "undefined" || typeof window.Tray !== "undefined") return true;

    // Algumas lojas Tray (ex: Accioly) não expõem nenhum dos globais acima. O tema carrega
    // o pageview e as imagens do CDN da Tray, então o host do script é o sinal que sobra.
    return (
      document.querySelector('script[src*="tcdn.com.br"], script[src*="tray.com.br"]') !== null
    );
  },

  isProductPage() {
    return matchesProductUrlOrBodyClass() || hasProductJsonLd();
  },

  getProductContext(): ProductContext {
    // JSON-LD não é garantido por padrão na Tray (depende de app de SEO/tema),
    // mas é a fonte mais confiável quando presente. Sem isso, não há dado seguro
    // para extrair — fallback em dataset do body fica como último recurso.
    const fromJsonLd = findProductJsonLd();
    if (fromJsonLd) return fromJsonLd;

    // URL padrão da Tray costuma ser /<slug>-p<id> — extrai o slug como fallback.
    const slugMatch = /\/([a-z0-9-]+)-p\d+(\/|$)/i.exec(window.location.pathname);

    const body = document.body;
    return {
      productId: body.dataset.productId ?? null,
      sku: body.dataset.sku ?? null,
      gtin: body.dataset.gtin ?? null,
      slug: body.dataset.slug ?? slugMatch?.[1] ?? null,
      slugPath: extractSlugPathFromUrl(window.location.href),
    };
  },
};
